<script setup lang="ts">
import {useRoute} from "vue-router";
import {ref} from "vue";
import ProductCategory from '~/components/pages/ProductCategory.vue';
import Collection from '~/components/pages/Collection.vue';
import Page from '~/components/pages/Page.vue';
import TipsAndAdvice from '~/components/pages/TipsAndAdvice.vue';
import City from '~/components/pages/City.vue';
import { classifyNetworkError } from '~/utils/networkError'
import { isRedirectFollowedError, fetchApiWithRedirects } from '~/utils/apiFetch'
import { slimPagePayload } from '~/utils/slimPagePayload.js'
import { useUserStore } from '~/store/userStore.js'
import { useLastVisitedProducts } from '~/store/lastVisitedProducts.js'

defineOptions({ name: 'CategoryPage' });

const {t} = useI18n();

definePageMeta({
    middleware: 'prefetch-page',
    // Keep one cached instance per category; filter segments must not remount the page
    // (otherwise Back/filter replace creates a fresh instance without UI filter state).
    key: (route) => {
        const slug = route.params.slug
        const parts = Array.isArray(slug) ? slug : [slug]
        const base = parts
            .filter((part) => part && !String(part).includes('='))
            .join('/')
        return `category-${base || 'index'}`
    },
});

const route = useRoute();
const router = useRouter();
const config = useRuntimeConfig();
const userStore = useUserStore();
const lastVisitedProducts = useLastVisitedProducts();

const slugParts = Array.isArray(route.params.slug)
    ? route.params.slug
    : [route.params.slug]
const slug = slugParts.filter(Boolean).join('/')
const queryString = new URLSearchParams(route.query).toString()
const cacheKey = `page-${slug}${queryString ? `-${queryString}` : ''}`

const REQUEST_CANCELLED_STATUS = 499

const { data, error } = await useAsyncData(
    cacheKey,
    async () => {
        const {authHeaders} = await import('~/composables/apiAuth.js')
        const headers = authHeaders({
            accept: 'application/json',
            'Accept-Language': config.public.locale,
            referer: config.public.baseURL,
            lastVisitedProducts: JSON.stringify(lastVisitedProducts.products),
        })

        const page = await fetchApiWithRedirects(`/page/${slug}`, {
            query: {...route.query},
            timeout: 60000,
            headers,
        }, 0, config.public) as Record<string, any>

        return slimPagePayload(page)
    },
    {
        getCachedData: (key, nuxtApp) =>
            nuxtApp.payload.data[key] ?? nuxtApp.static.data[key],
    }
)

if (error.value) {
    const err = error.value

    if (isRedirectFollowedError(err)) {
        throw createError({ statusCode: REQUEST_CANCELLED_STATUS, statusMessage: 'Request Cancelled', fatal: false })
    }

    const classification = classifyNetworkError(err)
    const statusCode = classification.statusCode

    if (classification.isAborted) {
        throw createError({ statusCode: REQUEST_CANCELLED_STATUS, statusMessage: 'Request Cancelled', fatal: false })
    }

    if (classification.isOffline) {
        throw createError({ statusCode: 503, statusMessage: 'Temporary Network Error', message: err.message, fatal: false })
    }

    if (statusCode === 410) {
        throw createError({ statusCode: 410, statusMessage: 'Page Is Gone' })
    }

    if (statusCode === 405) {
        throw createError({ statusCode: 404, statusMessage: 'Page Not Found' })
    }

    if (statusCode === 401) {
        await navigateTo(`/login?redirect=${router.currentRoute.value.fullPath}`)
        throw createError({ statusCode: REQUEST_CANCELLED_STATUS, statusMessage: 'Request Cancelled', fatal: false })
    }

    if ([404, 403].includes(statusCode)) {
        throw createError({ statusCode: 404, statusMessage: 'Page Not Found', message: err.message })
    }

    if (classification.isNetworkNoResponse) {
        throw createError({ statusCode: 503, statusMessage: 'Temporary Network Error', message: err.message })
    }

    throw createError({ statusCode: 500, statusMessage: 'Internal Error', message: err.message })
}

if (!data?.value) {
    throw createError({ statusCode: 503, statusMessage: 'Temporary Network Error' })
}

const payload = data.value
const pageData = payload.data
const ogImage = t('meta.domain') + '/img/og-default.jpg'
const hasCategoryFilters = payload.name === 'product-category'
    && slugParts.some(part => String(part).includes('='))
const categoryCanonicalPath = '/' + slugParts
    .filter(part => part && !String(part).includes('='))
    .join('/') + '/'
const categoryCanonicalUrl = useRequestURL().origin + categoryCanonicalPath

if (pageData.type === 'FAQ' && !route.path.includes('FAQ')) {
    navigateTo(`/faq/${slug}`);
}

useHead({
    title: pageData.meta_title ?? pageData.title,
    meta: [
        {hid: 'description', name: 'description', content: pageData.meta_description},
        {hid: 'og:image', property: 'og:image', content: ogImage},
        ...(hasCategoryFilters ? [{
            hid: 'robots',
            name: 'robots',
            content: 'noindex, nofollow',
        }] : []),
    ],
    link: [
        {key: 'image_src', rel: 'image_src', href: ogImage},
        ...(hasCategoryFilters ? [{
            key: 'canonical',
            rel: 'canonical',
            href: categoryCanonicalUrl,
        }] : []),
    ],
})

const name = ref(payload.name);
const pageType = ref(pageData.type);
const response_data = ref(pageData);
</script>

<template>
  <div>
    <TipsAndAdvice v-if="pageType === 'Tips And Advices'" :data="response_data"/>
    <template v-else>
      <ProductCategory v-if="name === 'product-category'" :data="response_data"/>
      <Collection v-else-if="name === 'collection'" :data="response_data"/>
      <Page v-else-if="name === 'page'" :data="response_data"/>
      <City v-else-if="name === 'city'" :data="response_data"/>
    </template>
  </div>
</template>
